import { useEffect } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { SplashScreen } from 'src/components/loading-screen';
import {
  isLegacyDashboardPath,
  buildTenantRedirectTarget,
} from 'src/lib/tenant-redirect';

import { useOrganization } from '@/context/organization-context';

/**
 * Global 404 handler. Old `/dashboard/...` links and bookmarks (minted before
 * tenancy moved into the URL) are forwarded into the active tenant prefix,
 * preserving the rest of the path + query. Org-scoped legacy paths
 * (settings/clients) go to `/dashboard/o/:org/...`; everything else goes to
 * `/dashboard/o/:org/c/:client/...`. The redirect uses the CURRENT tab's
 * tenant, so it stays in the right org/client. This is also what keeps OAuth
 * returns working: the backend still redirects to the old settings URL, which
 * lands here and is forwarded with `?success`/`?error` query intact.
 *
 * Loop-safe: a path already under `/dashboard/o/` is never redirected again, so
 * a genuinely-unknown prefixed path falls through to the not-found UI.
 */
export function LegacyTenantRedirect() {
  const navigate = useNavigate();
  const { activeOrganization, activeClient } = useOrganization();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isLegacyDashboard = isLegacyDashboardPath(pathname);

  useEffect(() => {
    if (!isLegacyDashboard) return;
    if (!activeOrganization?.id || !activeClient?.id) return;

    const queryHash =
      typeof window !== 'undefined'
        ? window.location.search + window.location.hash
        : '';
    const target = buildTenantRedirectTarget(
      pathname,
      queryHash,
      activeOrganization.id,
      activeClient.id,
    );

    navigate({ to: target, replace: true });
  }, [
    isLegacyDashboard,
    pathname,
    activeOrganization?.id,
    activeClient?.id,
    navigate,
  ]);

  // While a legacy path resolves (or waits for the tenant to load), show splash.
  if (isLegacyDashboard) {
    return <SplashScreen />;
  }

  // Genuinely unknown path.
  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        textAlign: 'center',
        px: 3,
      }}
    >
      <Typography variant="h4">Page not found</Typography>
      <Typography variant="body2" color="text.secondary">
        The page you are looking for doesn&apos;t exist or has moved.
      </Typography>
      <Button variant="contained" onClick={() => navigate({ to: paths.dashboard.root })}>
        Back to dashboard
      </Button>
    </Box>
  );
}
